/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @typescript-eslint/restrict-template-expressions */
import React, { useContext, useEffect, useState } from 'react';
import cx from 'classnames';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { Image } from 'components/shared/image/Image';
import { Loader } from 'helpers/Loader';
import { apiRequest } from 'helpers/apiClient';

import './InfoDetails.scss';
import { LoremIpsum } from 'react-lorem-ipsum';
import { Project } from 'pages/projects/projectComponent/Project';
import { Button } from '@chakra-ui/react';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { RadarContext } from 'navigation/context';
import { isAdmin } from 'components/shared/helpers/auth';

interface Props {
  tableName: string;
  relation: string;
}

export const InfoDetails: React.FC<Props> = ({ tableName, relation }) => {
  const [item, setItem] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [projects, setProjects] = useState<any>([]);
  const [selectedSection, setSelectedSection] = useState<string>('overview');
  const { id } = useParams();

  const path = useLocation().pathname;
  const isTechPage = path.includes('technologies');
  const navigate = useNavigate();

  const { setProjectsToEdit } = useContext(RadarContext);

  const fetchItem = async (): Promise<any> => {
    try {
      const resource =
        tableName === 'technologies' ? 'technology' : 'disaster-type';
      const { data } = await apiRequest<{ data: any }>(
        `public/details/${resource}/${encodeURIComponent(id || '')}`
      );
      setItem(data);
      await getProjects();
    } catch (error) {
      console.error('Error fetching item details:', error);
    }
  };

  const getProjects = async (): Promise<any> => {
    try {
      const resource =
        relation === 'tech_projects'
          ? 'technology-projects'
          : 'disaster-projects';
      const { data } = await apiRequest<{ data: any[] }>(
        `public/details/${resource}/${encodeURIComponent(id || '')}`
      );
      setProjects(data);
    } catch (error) {
      console.error('Error fetching related projects:', error);
    }
  };

  useEffect(() => {
    fetchItem();
  }, []);

  const handleScroll = (id: string): void => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView();
    }
  };

  const handleEdit = (): void => {
    setProjectsToEdit(projects);
    navigate(`${path}/edit`);
  };

  const handleDelete = async (): Promise<void> => {
    setLoading(true);
    const redirectRoute = isTechPage ? '/technologies' : '/disasters';
    if (!confirm('Are you sure you want to delete this item?')) return;

    try {
      const resource = isTechPage ? 'technology' : 'disaster-type';
      await apiRequest(
        `admin/info/${resource}/${encodeURIComponent(item.slug)}`,
        {
          method: 'DELETE'
        }
      );
      localStorage.removeItem(
        isTechPage ? 'drr-technologies' : 'drr-disaster-types'
      );
      alert('Deleted successfully');
      navigate(redirectRoute);
    } catch (error) {
      console.error('Error deleting information record:', error);
      alert('There was an error. Please try again');
    }
    setLoading(false);
  };

  return item && !loading ? (
    <div className='itemDetailsPage'>
      <div className='itemHero'>
        <div className='itemTitle'>
          <span>{item.name}</span>
          {item?.source && (
            <a
              href={item.source}
              target='_blank'
              rel='noopener noreferrer'
              className='seeitem'
            >
              GET MORE INFORMATION
            </a>
          )}
        </div>
        <div className='itemImg'>
          <Image imgUrl={item.img_url} />
        </div>
      </div>
      {isAdmin() && (
        <div className='infoActions'>
          <Button
            leftIcon={<EditIcon />}
            bgColor='#2868AC'
            variant='solid'
            color='white'
            _hover={{ bg: '#6895C4' }}
            _active={{}}
            _focus={{}}
            onClick={handleEdit}
          >
            Edit
          </Button>
          <Button
            leftIcon={<DeleteIcon />}
            bgColor='#C1391D'
            variant='solid'
            color='white'
            _hover={{ bg: '#D37460' }}
            _active={{}}
            _focus={{}}
            data-testid='delete-info'
            onClick={() => {
              handleDelete();
            }}
          >
            Delete
          </Button>
        </div>
      )}
      <div className='itemBody'>
        <div className='itemToc'>
          <Link
            to='#'
            className={cx({ bolden: selectedSection === 'overview' })}
            onClick={() => {
              handleScroll('item-details-section');
              setSelectedSection('overview');
            }}
          >
            Overview
          </Link>
          <Link
            to='#'
            className={cx({ bolden: selectedSection === 'projects' })}
            onClick={() => {
              handleScroll('item-projects-section');
              setSelectedSection('projects');
            }}
          >
            Projects
          </Link>
        </div>
        <div className='itemContent'>
          <section id='item-details-section'>
            <span className='itemDetailsTitle'> {item.name} </span>

            {item.description ? (
              item.description.split('##').map((i: string, idx: number) => (
                <p className='itemDetailsContent' key={idx}>
                  {i}
                </p>
              ))
            ) : (
              <p className='itemDetailsContent'>
                <LoremIpsum p={2} />
              </p>
            )}
          </section>
          <hr className='separater' />
          <section id='item-projects-section'>
            <span className='itemDetailsTitle'> Projects </span>
            <div className='projectContainer projectsList'>
              {projects.map((project: any) => (
                <div key={project.id}>
                  <Project project={project} />
                  <hr />
                </div>
              ))}
            </div>
          </section>
          <hr className='separater' />
        </div>
      </div>
    </div>
  ) : (
    <Loader />
  );
};
